import { apiClient } from './client';
import type {
  Project,
  CreateProjectRequest,
  UpdateProjectRequest,
  DashboardData,
} from '@/shared/types/project';

// Backend project response type
interface BackendProject {
  id: string;
  name: string;
  description?: string;
  website?: string;
  website_display?: string;
  settings?: {
    metrika_counter_id?: string;
    webmaster_host_id?: string;
  };
  created_at: string;
  updated_at: string;
  // Multi-organization fields
  organization_id?: string;
  organization_name?: string;
  user_role?: string;
}

// Map backend project to frontend format
function mapProject(p: BackendProject): Project {
  return {
    id: p.id,
    name: p.name,
    website: p.website || '',
    websiteDisplay: p.website_display || p.website || '',
    metrikaCounterId: p.settings?.metrika_counter_id || '',
    webmasterHostId: p.settings?.webmaster_host_id,
    isActive: true,
    createdAt: p.created_at,
    updatedAt: p.updated_at,
    // Multi-organization fields
    organizationId: p.organization_id,
    organizationName: p.organization_name,
    userRole: p.user_role,
  };
}

/**
 * Projects API methods.
 */
export const projectsApi = {
  /**
   * Get all user projects.
   */
  getProjects: async (): Promise<Project[]> => {
    const response = await apiClient.get<{ projects: BackendProject[] }>('/projects');
    return (response.data.projects || []).map(mapProject);
  },

  /**
   * Get single project by ID.
   */
  getProject: async (id: string): Promise<Project> => {
    const response = await apiClient.get<BackendProject>(`/projects/${id}`);
    return mapProject(response.data);
  },

  /**
   * Create new project.
   */
  createProject: async (data: CreateProjectRequest): Promise<Project> => {
    const backendData = {
      name: data.name,
      website: data.website,
    };
    const response = await apiClient.post<BackendProject>('/projects', backendData);
    return mapProject(response.data);
  },

  /**
   * Update existing project.
   */
  updateProject: async (id: string, data: UpdateProjectRequest): Promise<Project> => {
    const response = await apiClient.patch<BackendProject>(`/projects/${id}`, data);
    return mapProject(response.data);
  },

  /**
   * Delete project.
   */
  deleteProject: async (id: string): Promise<void> => {
    await apiClient.delete(`/projects/${id}`);
  },

  /**
   * Get dashboard metrics for a project.
   */
  getDashboard: async (
    projectId: string,
    dateFrom?: string,
    dateTo?: string
  ): Promise<DashboardData> => {
    const response = await apiClient.get<DashboardData>(`/projects/${projectId}/dashboard`, {
      params: { date_from: dateFrom, date_to: dateTo },
    });
    return response.data;
  },

  /**
   * Verify Yandex Metrika counter access.
   */
  verifyCounter: async (counterId: string): Promise<{ valid: boolean; error?: string }> => {
    const response = await apiClient.post<{ valid: boolean; error?: string }>(
      '/projects/verify-counter',
      { counterId }
    );
    return response.data;
  },
};
